import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { PlusCircle, Copy, Trash2, Package } from 'lucide-react';

const UNIT_TYPES = [
  { v: 'sucursal_propia', l: 'Sucursal propia' },
  { v: 'franquicia', l: 'Franquicia' },
  { v: 'distribuidor', l: 'Distribuidor' },
  { v: 'punto_venta', l: 'Punto de venta' },
  { v: 'showroom', l: 'Showroom' },
  { v: 'otro', l: 'Otro' },
];

const EMPTY = {
  project_name: '', company_name: '', industry: '', subcategory: '',
  description: '', unit_type: 'sucursal_propia',
  target_customer: '', age_range: '', socioeconomic: '',
  avg_ticket: '', gross_margin: '', max_investment: '', max_rent: '',
  sqm_desired: '', employees_estimated: '',
  min_monthly_sales: '', target_monthly_sales: '',
  needs_parking: false, depends_foot_traffic: false, depends_vehicle_traffic: false,
  depends_tourism: false, depends_digital: false,
  known_competitors: '', keywords: '',
};

export default function BusinessProfiles() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/business-profiles').then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        user_id: '',
        avg_ticket: form.avg_ticket ? Number(form.avg_ticket) : null,
        gross_margin: form.gross_margin ? Number(form.gross_margin) : null,
        max_investment: form.max_investment ? Number(form.max_investment) : null,
        max_rent: form.max_rent ? Number(form.max_rent) : null,
        sqm_desired: form.sqm_desired ? Number(form.sqm_desired) : null,
        employees_estimated: form.employees_estimated ? Number(form.employees_estimated) : null,
        min_monthly_sales: form.min_monthly_sales ? Number(form.min_monthly_sales) : null,
        target_monthly_sales: form.target_monthly_sales ? Number(form.target_monthly_sales) : null,
        known_competitors: form.known_competitors ? form.known_competitors.split(',').map((s) => s.trim()).filter(Boolean) : [],
        keywords: form.keywords ? form.keywords.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post('/business-profiles', payload);
      toast.success('Perfil de negocio guardado');
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar perfil?')) return;
    await api.delete(`/business-profiles/${id}`);
    load();
  };

  const useInNewAnalysis = (profile) => {
    localStorage.setItem('strategia_prefill_profile', JSON.stringify(profile));
    toast.info('Perfil listo — completa la ubicación en el wizard');
    nav('/analyses/new');
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="label-caps">Perfiles reutilizables</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">
            Perfiles de negocio
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Guarda perfiles de proyectos de expansión para reutilizarlos en múltiples análisis territoriales.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-profile-btn" className="bg-blue-900 hover:bg-blue-800 h-11 px-5">
              <PlusCircle size={16} className="mr-2" /> Nuevo perfil
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuevo perfil de negocio</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Proyecto *</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Empresa *</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Giro *</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1" /></div>
              <div><Label>Subcategoría</Label><Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="mt-1" /></div>
              <div><Label>Tipo de unidad</Label>
                <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIT_TYPES.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>NSE objetivo</Label><Input value={form.socioeconomic} onChange={(e) => setForm({ ...form, socioeconomic: e.target.value })} className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
              <div><Label>Ticket promedio ($)</Label><Input type="number" value={form.avg_ticket} onChange={(e) => setForm({ ...form, avg_ticket: e.target.value })} className="mt-1" /></div>
              <div><Label>Margen bruto (%)</Label><Input type="number" value={form.gross_margin} onChange={(e) => setForm({ ...form, gross_margin: e.target.value })} className="mt-1" /></div>
              <div><Label>Inversión máxima ($)</Label><Input type="number" value={form.max_investment} onChange={(e) => setForm({ ...form, max_investment: e.target.value })} className="mt-1" /></div>
              <div><Label>Renta mensual máx ($)</Label><Input type="number" value={form.max_rent} onChange={(e) => setForm({ ...form, max_rent: e.target.value })} className="mt-1" /></div>
              <div><Label>Venta mensual mínima ($)</Label><Input type="number" value={form.min_monthly_sales} onChange={(e) => setForm({ ...form, min_monthly_sales: e.target.value })} className="mt-1" /></div>
              <div><Label>Venta objetivo ($)</Label><Input type="number" value={form.target_monthly_sales} onChange={(e) => setForm({ ...form, target_monthly_sales: e.target.value })} className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Palabras clave (coma)</Label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="perfume, fragancia, cosmetico" className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Competidores conocidos (coma)</Label><Input value={form.known_competitors} onChange={(e) => setForm({ ...form, known_competitors: e.target.value })} className="mt-1" /></div>
              <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-slate-200">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.needs_parking} onCheckedChange={(v) => setForm({ ...form, needs_parking: !!v })} />Estacionamiento</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.depends_foot_traffic} onCheckedChange={(v) => setForm({ ...form, depends_foot_traffic: !!v })} />Tráfico peatonal</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.depends_vehicle_traffic} onCheckedChange={(v) => setForm({ ...form, depends_vehicle_traffic: !!v })} />Tráfico vehicular</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.depends_tourism} onCheckedChange={(v) => setForm({ ...form, depends_tourism: !!v })} />Turismo</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.depends_digital} onCheckedChange={(v) => setForm({ ...form, depends_digital: !!v })} />Venta digital</label>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-profile-btn" className="bg-blue-900 hover:bg-blue-800">Guardar perfil</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <div className="text-slate-500">Cargando…</div>}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center bg-white border border-slate-200 p-12">
            <Package size={40} className="mx-auto text-slate-300 mb-2" />
            <div className="text-slate-700 font-semibold">Aún no tienes perfiles guardados</div>
            <div className="text-sm text-slate-500 mt-1">Crea uno para reutilizarlo en varios análisis territoriales.</div>
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} data-testid={`profile-card-${p.id}`} className="bg-white border border-slate-200 p-5 hover:border-blue-900 transition-colors">
            <div className="label-caps">{p.industry}</div>
            <h3 className="font-display text-xl font-bold mt-1 text-slate-900">{p.project_name}</h3>
            <div className="text-sm text-slate-600">{p.company_name} · {UNIT_TYPES.find((u) => u.v === p.unit_type)?.l}</div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <div><span className="text-slate-500">Ticket:</span> ${p.avg_ticket || '—'}</div>
              <div><span className="text-slate-500">Inversión:</span> ${p.max_investment?.toLocaleString('es-MX') || '—'}</div>
              <div><span className="text-slate-500">NSE:</span> {p.socioeconomic || '—'}</div>
              <div><span className="text-slate-500">Turismo:</span> {p.depends_tourism ? 'Sí' : 'No'}</div>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <Button onClick={() => applyProfile(p)} data-testid={`use-profile-${p.id}`} size="sm" className="flex-1 bg-blue-900 hover:bg-blue-800 h-9">
                <Copy size={13} className="mr-2" /> Usar en análisis
              </Button>
              <button onClick={() => del(p.id)} data-testid={`delete-profile-${p.id}`} className="p-2 hover:bg-red-100 rounded text-red-700"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
